import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import {
  SelectReviewSetType,
  addUserToSavedSearchSuccess,
  addUserToSavedSearchSuccessPayload,
  addUserToViewersFailure,
  addUserToViewersPayload,
  addUserToViewersSuccess,
  addUsersToReviewersFailure,
  addUsersToReviewersPayload,
  addUsersToReviewersSuccess,
  addUsersToViewersFailure,
  addUsersToViewersPayload,
  addUsersToViewersSuccess,
  deleteSavedSearchFailure,
  deleteSavedSearchFulfill,
  deleteSavedSearchRequest,
  deleteSavedSearchSuccess,
  fetchOwnSavedSearchesSuccess,
  fetchOwnSavedSearchesViewersFailure,
  fetchOwnSavedSearchesViewersFulfill,
  fetchOwnSavedSearchesViewersRequest,
  fetchOwnSavedSearchesViewersSuccess,
  fetchSavedSearchesFailure,
  fetchSavedSearchesFulfill,
  fetchSavedSearchesRequest,
  fetchSavedSearchesSuccess,
  fetchSingleSavedSearchSuccess,
  openMessagesModal,
  openSubsamplesModal,
  removeUserFromSavedSearchPayload,
  removeUserFromSavedSearchSuccess,
  removeUserFromSavedSearchSuccessPayload,
  removeUserFromViewersFailure,
  removeUserFromViewersSuccess,
  selectReviewSet,
  upsertSavedSearchFailure,
  upsertSavedSearchFulfill,
  upsertSavedSearchParamsFailure,
  upsertSavedSearchParamsRequest,
  upsertSavedSearchParamsSuccess,
  upsertSavedSearchRequest,
  upsertSavedSearchSuccess,
} from 'actions';
import type { API, ErrorObject, NormalizedResource, SavedSearch } from 'types';

type SavedSearchState = {
  loading: string[];
  error: ErrorObject | null;
  items: NormalizedResource<SavedSearch>;
  mySearches: NormalizedResource<SavedSearch>;
  count: number;
  selectedItem: SavedSearch | null;
  subsamplesModalOpen: boolean;
  messagesModalOpen: boolean;
};

type SavedSearchReducer<P = void> = (
  state: SavedSearchState,
  payload: PayloadAction<P>
) => SavedSearchState | void;

const defaultState: SavedSearchState = {
  loading: [],
  error: null,
  items: {},
  mySearches: {},
  count: 0,
  selectedItem: null,
  subsamplesModalOpen: false,
  messagesModalOpen: false,
};

const handleFetchSavedSearchesRequest: SavedSearchReducer = (state) => ({
  ...state,
  error: null,
  loading: [...state.loading, fetchSavedSearchesRequest.toString()],
});

const handleFetchSavedSearchesFailure: SavedSearchReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
});

const handleFetchSavedSearchesSuccess: SavedSearchReducer<API.WrappedAPIResponse<SavedSearch>> = (
  state,
  { payload }
) => {
  const normalizedSavedSearches: NormalizedResource<SavedSearch> = {};
  payload.records.forEach((savedSearch) => {
    normalizedSavedSearches[savedSearch.uuid] = savedSearch;
  });
  return {
    ...state,
    items: normalizedSavedSearches,
    count: payload.count,
  };
};

const handleFetchOwnSavedSearchesSuccess: SavedSearchReducer<
  API.WrappedAPIResponse<SavedSearch>
> = (state, { payload }) => {
  const normalizedSavedSearches: NormalizedResource<SavedSearch> = {};
  payload.records.forEach((savedSearch) => {
    normalizedSavedSearches[savedSearch.uuid] = savedSearch;
  });
  return {
    ...state,
    mySearches: normalizedSavedSearches,
  };
};

const handleFetchSavedSearchesFulfill: SavedSearchReducer = (state) => ({
  ...state,
  loading: state.loading.filter((s) => s !== fetchSavedSearchesRequest.toString()),
});

const handleFetchOwnSavedSearchesViewersRequest: SavedSearchReducer = (state) => ({
  ...state,
  error: null,
  loading: [...state.loading, fetchOwnSavedSearchesViewersRequest.toString()],
});

const handleFetchOwnViewerSavedSearchesSuccess: SavedSearchReducer<
  API.WrappedAPIResponse<SavedSearch>
> = (state, { payload }) => {
  const normalizedSavedSearches: NormalizedResource<SavedSearch> = {};
  payload.records.forEach((savedSearch) => {
    normalizedSavedSearches[savedSearch.uuid] = savedSearch;
  });
  return {
    ...state,
    items: normalizedSavedSearches,
    count: payload.count,
  };
};

const handleFetchOwnViewerSavedSearchesFulfill: SavedSearchReducer = (state) => ({
  ...state,
  loading: state.loading.filter((s) => s !== fetchOwnSavedSearchesViewersRequest.toString()),
});

const handleFetchOwnViewerSavedSearchesFailure: SavedSearchReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
});

const handleUpsertSavedSearchFailure: SavedSearchReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
});

const handleUpsertSavedSearchRequest: SavedSearchReducer = (state: SavedSearchState) => ({
  ...state,
  error: null,
  loading: [...state.loading, upsertSavedSearchRequest.toString()],
});
const handleUpsertSavedSearchSuccess: SavedSearchReducer<SavedSearch> = (state, { payload }) => {
  const newSavedSearch = state.items[payload.uuid];

  if (newSavedSearch) {
    state.items[payload.uuid] = payload;
    state.selectedItem = payload;
    return state;
  }

  const parentSearch = state.items[payload.parent_uuid];
  if (parentSearch?.children) {
    parentSearch.children = parentSearch.children?.map((c) =>
      c.uuid === payload.uuid ? payload : c
    );
    state.selectedItem = payload;
    return state;
  }

  state.selectedItem = payload;
  state.items[payload.uuid] = payload;
  return state;
};

const handleUpsertSavedSearchFulfill: SavedSearchReducer = (state) => ({
  ...state,
  loading: [...state.loading, upsertSavedSearchRequest.toString()],
});

const handleDeleteSavedSearchRequest: SavedSearchReducer = (state) => ({
  ...state,
  error: null,
  loading: [...state.loading, deleteSavedSearchRequest.toString()],
});

const handleDeleteSavedSearchSuccess: SavedSearchReducer<string> = (state, { payload }) => {
  const id = payload;
  const savedSearches = state.items;
  let selected = state.selectedItem as SavedSearch;

  if (savedSearches[id]) {
    delete savedSearches[id];

    if (selected && selected.uuid === id) {
      [selected] = [...Object.values(savedSearches)];
    }

    return state;
  }

  if (selected?.parent_uuid) {
    const parentId = selected.parent_uuid;
    if (savedSearches[parentId]) {
      const children = savedSearches[parentId].children?.filter((c) => c.uuid !== id);
      savedSearches[parentId].children = children;

      if (selected && selected.uuid === id) {
        [selected] = Object.values(savedSearches);
      }

      return state;
    }
  }

  return state;
};

const handleDeleteSavedSearchFailure: SavedSearchReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
});

const handleDeleteSavedSearchFulfill: SavedSearchReducer = (state) => ({
  ...state,
  loading: state.loading.filter((s) => s !== deleteSavedSearchRequest.toString()),
});

const handleAddUserToSavedSearchSuccess: SavedSearchReducer<addUserToSavedSearchSuccessPayload> = (
  state,
  { payload }
) => {
  const { savedSearch, user } = payload;
  const newSavedSearch = state.items[savedSearch.uuid];

  if (!newSavedSearch) {
    const parentSearch = state.items[savedSearch.parent_uuid];
    const childIndex = parentSearch.children?.findIndex((c) => c.uuid === savedSearch.uuid);

    if (
      parentSearch &&
      childIndex !== undefined &&
      childIndex >= 0 &&
      parentSearch.children?.[childIndex]
    ) {
      parentSearch.children?.[childIndex]?.users?.push(user);
      if (state.selectedItem) {
        state.selectedItem = {
          ...state.selectedItem,
          users: [...(state.selectedItem?.users || []), user],
        };
      }
    }

    return state;
  }

  newSavedSearch.audit_logs = savedSearch.audit_logs;
  newSavedSearch.users = savedSearch.users;
  state.selectedItem = newSavedSearch;

  return state;
};

const handleUsersToReviewersSuccess: SavedSearchReducer<addUsersToReviewersPayload> = (
  state,
  { payload }
) => {
  const { savedSearchId, users } = payload;
  const savedSearch = state.items[savedSearchId];

  if (savedSearch) {
    users.forEach((user) => {
      if (!savedSearch.users?.find((u) => u.uuid === user.uuid)) {
        if (savedSearch.users) {
          savedSearch.users.push(user);
        } else {
          savedSearch.users = [user];
        }
      }
    });
    return state;
  }

  const foundParent = Object.values(state.items).find((search) =>
    search.children?.some((child) => child.uuid === savedSearchId)
  );
  const foundChild = foundParent?.children?.find((child) => child.uuid === savedSearchId);

  if (foundParent && foundChild) {
    users.forEach((user) => {
      if (!foundChild.users?.find((u) => u.uuid === user.uuid)) {
        if (foundChild.users) {
          foundChild.users.push(user);
        } else {
          foundChild.users = [user];
        }
      }
    });
    return state;
  }

  return state;
};

const handleUsersToReviewersFailure: SavedSearchReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
});

const handleUsersToViewersSuccess: SavedSearchReducer<addUsersToViewersPayload> = (
  state,
  { payload }
) => {
  const { savedSearchId, users } = payload;
  const savedSearch = state.items[savedSearchId];

  if (savedSearch) {
    users.forEach((user) => {
      if (!savedSearch.viewers?.find((u) => u.uuid === user.uuid)) {
        if (savedSearch.viewers) {
          savedSearch.viewers.push(user);
        } else {
          savedSearch.viewers = [user];
        }
      }
    });
    return state;
  }

  const foundParent = Object.values(state.items).find((search) =>
    search.children?.some((child) => child.uuid === savedSearchId)
  );
  const foundChild = foundParent?.children?.find((child) => child.uuid === savedSearchId);

  if (foundParent && foundChild) {
    users.forEach((user) => {
      if (!foundChild.viewers?.find((u) => u.uuid === user.uuid)) {
        if (foundChild.viewers) {
          foundChild.viewers.push(user);
        } else {
          foundChild.viewers = [user];
        }
      }
    });
    return state;
  }

  return state;
};

const handleUsersToViewersFailure: SavedSearchReducer<ErrorObject> = (state, { payload }) => ({
  ...state,
  error: payload,
});

const handleRemoveUserFromSavedSearchSuccess: SavedSearchReducer<
  removeUserFromSavedSearchSuccessPayload
> = (state, { payload }) => {
  const { savedSearch } = payload;
  const newSavedSearch = state.items[savedSearch.uuid];

  if (!newSavedSearch) {
    const parentSearch = state.items[savedSearch.parent_uuid];
    const childIndex = parentSearch.children?.findIndex((c) => c.uuid === savedSearch.uuid);

    if (
      parentSearch &&
      childIndex !== undefined &&
      childIndex >= 0 &&
      parentSearch.children?.[childIndex]
    ) {
      parentSearch.children[childIndex].users = savedSearch.users;
      if (state.selectedItem) {
        state.selectedItem = {
          ...state.selectedItem,
          users: savedSearch.users,
        };
      }
    }

    return state;
  }

  newSavedSearch.audit_logs = savedSearch.audit_logs;
  newSavedSearch.users = savedSearch.users;
  state.selectedItem = newSavedSearch;

  return state;
};

const handleAddViewerToSavedSearchSuccess: SavedSearchReducer<addUserToViewersPayload> = (
  state,
  { payload }
) => {
  const { savedSearchId, user } = payload;
  const savedSearch = state.items[savedSearchId];

  if (savedSearch) {
    if (!savedSearch.viewers?.find((u) => u.uuid === user.uuid)) {
      if (savedSearch.viewers) {
        savedSearch.viewers.push(user);
      } else {
        savedSearch.viewers = [user];
      }
    }
    return state;
  }

  const foundParent = Object.values(state.items).find((search) =>
    search.children?.some((child) => child.uuid === savedSearchId)
  );
  const foundChild = foundParent?.children?.find((child) => child.uuid === savedSearchId);

  if (foundParent && foundChild) {
    if (!foundChild.viewers?.find((u) => u.uuid === user.uuid)) {
      if (foundChild.viewers) {
        foundChild.viewers.push(user);
      } else {
        foundChild.viewers = [user];
      }
    }
    return state;
  }

  return state;
};

const handleRemoveViewerToSavedSearchFailure: SavedSearchReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
});

const handleRemoveViewerFromSavedSearchSuccess: SavedSearchReducer<
  removeUserFromSavedSearchPayload
> = (state, { payload }) => {
  const { savedSearchId, user } = payload;
  const savedSearch = state.items[savedSearchId];

  if (savedSearch) {
    savedSearch.viewers = savedSearch.users?.filter((u) => u.uuid !== user.uuid);
    return state;
  }

  const foundParent = Object.values(state.items).find((search) =>
    search.children?.some((child) => child.uuid === savedSearchId)
  );
  const foundChild = foundParent?.children?.find((child) => child.uuid === savedSearchId);

  if (foundParent && foundChild) {
    foundChild.viewers = foundChild.users?.filter((u) => u.uuid !== user.uuid);
    return state;
  }

  return state;
};

const handleRemoveViewerFromSavedSearchFailure: SavedSearchReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
});

const handleSelectReviewSet: SavedSearchReducer<SelectReviewSetType> = (state, { payload }) => {
  const { reviewSet } = payload;

  return {
    ...state,
    selectedItem: reviewSet,
  };
};

const handleFetchSingleSavedSearchSuccess: SavedSearchReducer<SavedSearch> = (
  state,
  { payload }
) => ({
  ...state,
  items: { ...state.items, [payload.uuid]: payload },
});

const handleUpsertSavedSearchParamsRequest: SavedSearchReducer = (state) => ({
  ...state,
  loading: [...state.loading, upsertSavedSearchParamsRequest.toString()],
});

const handleUpsertSavedSearchParamsSuccess: SavedSearchReducer<SavedSearch> = (
  state,
  { payload }
) => ({
  ...state,
  items: { ...state.items, [payload.uuid]: payload },
  selectedItem: payload,
  loading: state.loading.filter((s) => s !== upsertSavedSearchParamsRequest.toString()),
});

const handleUpsertSavedSearchParamsFailure: SavedSearchReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== upsertSavedSearchParamsRequest.toString()),
});

const handleOpenSubsamplesModal: SavedSearchReducer = (state) => ({
  ...state,
  subsamplesModalOpen: !state.subsamplesModalOpen,
});

const handleOpenMessagesModal: SavedSearchReducer = (state) => ({
  ...state,
  messagesModalOpen: !state.messagesModalOpen,
});

const handlers = {
  [fetchSavedSearchesRequest.toString()]: handleFetchSavedSearchesRequest,
  [fetchSavedSearchesSuccess.toString()]: handleFetchSavedSearchesSuccess,
  [fetchSavedSearchesFailure.toString()]: handleFetchSavedSearchesFailure,
  [fetchSavedSearchesFulfill.toString()]: handleFetchSavedSearchesFulfill,
  [fetchOwnSavedSearchesSuccess.toString()]: handleFetchOwnSavedSearchesSuccess,
  [upsertSavedSearchRequest.toString()]: handleUpsertSavedSearchRequest,
  [upsertSavedSearchSuccess.toString()]: handleUpsertSavedSearchSuccess,
  [upsertSavedSearchFailure.toString()]: handleUpsertSavedSearchFailure,
  [upsertSavedSearchFulfill.toString()]: handleUpsertSavedSearchFulfill,
  [deleteSavedSearchRequest.toString()]: handleDeleteSavedSearchRequest,
  [deleteSavedSearchSuccess.toString()]: handleDeleteSavedSearchSuccess,
  [deleteSavedSearchFailure.toString()]: handleDeleteSavedSearchFailure,
  [deleteSavedSearchFulfill.toString()]: handleDeleteSavedSearchFulfill,
  [addUserToSavedSearchSuccess.toString()]: handleAddUserToSavedSearchSuccess,
  [addUsersToReviewersSuccess.toString()]: handleUsersToReviewersSuccess,
  [addUsersToReviewersFailure.toString()]: handleUsersToReviewersFailure,
  [removeUserFromSavedSearchSuccess.toString()]: handleRemoveUserFromSavedSearchSuccess,
  [selectReviewSet.toString()]: handleSelectReviewSet,
  [fetchSingleSavedSearchSuccess.toString()]: handleFetchSingleSavedSearchSuccess,
  [addUserToViewersSuccess.toString()]: handleAddViewerToSavedSearchSuccess,
  [addUserToViewersFailure.toString()]: handleRemoveViewerToSavedSearchFailure,
  [removeUserFromViewersSuccess.toString()]: handleRemoveViewerFromSavedSearchSuccess,
  [removeUserFromViewersFailure.toString()]: handleRemoveViewerFromSavedSearchFailure,
  [fetchOwnSavedSearchesViewersRequest.toString()]: handleFetchOwnSavedSearchesViewersRequest,
  [fetchOwnSavedSearchesViewersSuccess.toString()]: handleFetchOwnViewerSavedSearchesSuccess,
  [fetchOwnSavedSearchesViewersFailure.toString()]: handleFetchOwnViewerSavedSearchesFailure,
  [fetchOwnSavedSearchesViewersFulfill.toString()]: handleFetchOwnViewerSavedSearchesFulfill,
  [addUsersToViewersSuccess.toString()]: handleUsersToViewersSuccess,
  [addUsersToViewersFailure.toString()]: handleUsersToViewersFailure,
  [upsertSavedSearchParamsRequest.toString()]: handleUpsertSavedSearchParamsRequest,
  [upsertSavedSearchParamsSuccess.toString()]: handleUpsertSavedSearchParamsSuccess,
  [upsertSavedSearchParamsFailure.toString()]: handleUpsertSavedSearchParamsFailure,
  [openSubsamplesModal.toString()]: handleOpenSubsamplesModal,
  [openMessagesModal.toString()]: handleOpenMessagesModal,
};

const savedSearchesReducer = createReducer(defaultState, handlers);

export default savedSearchesReducer;
